import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ArrowLeft, Check, Loader2, Share2, Sparkles, Target, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { trackEvent, getAnonId } from '@/lib/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
    fragile: 'bg-red-50 text-red-700 border-red-200',
    structuration: 'bg-amber-50 text-amber-800 border-amber-200',
    prometteur: 'bg-blue-50 text-blue-700 border-blue-200',
    solide: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const barColor = (pct) => pct >= 67 ? 'bg-emerald-500' : pct >= 34 ? 'bg-amber-500' : 'bg-red-400';

function ScoreCircle({ total }) {
    const r = 66, c = 2 * Math.PI * r;
    return (
        <div className="relative w-44 h-44 mx-auto" data-testid="score-circle">
            <svg viewBox="0 0 160 160" className="w-full h-full -rotate-90">
                <circle cx="80" cy="80" r={r} fill="none" stroke="currentColor" className="text-surface-muted" strokeWidth="12" />
                <circle cx="80" cy="80" r={r} fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round"
                    className="text-primary transition-all duration-1000"
                    strokeDasharray={c} strokeDashoffset={c - (c * total) / 100} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-heading text-5xl" data-testid="score-total">{total}</span>
                <span className="text-xs text-muted-foreground">/ 100</span>
            </div>
        </div>
    );
}

function ResultView({ result, shareUrl, onRestart }) {
    const levelCls = LEVEL_COLORS[result.level?.key] || LEVEL_COLORS.structuration;
    const [aiText, setAiText] = useState(result.ai_analysis || null);
    const [aiBusy, setAiBusy] = useState(false);
    const copyLink = () => {
        navigator.clipboard?.writeText(shareUrl).then(
            () => toast.success('Lien du résultat copié !'),
            () => toast.error('Copie impossible — copiez l\'URL manuellement'));
    };
    const getAiPlan = async () => {
        setAiBusy(true);
        try {
            const r = await axios.post(`${API}/project-score/${result.id}/ai-analysis`);
            setAiText(r.data.analysis);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setAiBusy(false); }
    };
    return (
        <div className="max-w-3xl mx-auto" data-testid="score-result">
            <div className="text-center mb-10">
                <div className="eyebrow mb-3">Votre African Valley Project Score™</div>
                {result.project_name && <h1 className="font-heading text-3xl mb-2">{result.project_name}</h1>}
                <ScoreCircle total={result.total} />
                <div className={`inline-flex items-center gap-2 mt-5 px-4 py-1.5 rounded-full border text-sm font-medium ${levelCls}`} data-testid="score-level">
                    {result.level?.label}
                </div>
                <p className="text-foreground/70 mt-4 max-w-xl mx-auto leading-relaxed text-sm">{result.level?.advice}</p>
            </div>

            <div className="card-soft p-7 mb-6">
                <h2 className="font-heading text-lg mb-5">Détail par dimension</h2>
                <div className="space-y-3.5" data-testid="score-dimensions">
                    {result.per_dimension.map(d => (
                        <div key={d.key} className="flex items-center gap-3" data-testid={`score-dim-${d.key}`}>
                            <span className="text-sm w-52 shrink-0 text-foreground/80">{d.label}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-surface-muted overflow-hidden">
                                <div className={`h-full ${barColor(d.percent)} rounded-full transition-all duration-700`} style={{ width: `${d.percent}%` }} />
                            </div>
                            <span className="text-xs font-mono w-16 text-right text-muted-foreground">{d.points}/{d.weight}</span>
                        </div>
                    ))}
                </div>
            </div>

            {result.recommendations?.length > 0 && (
                <div className="card-soft p-7 mb-8 border-primary/30" data-testid="score-recommendations">
                    <h2 className="font-heading text-lg mb-1 flex items-center gap-2"><Target size={17} className="text-primary" /> Vos priorités de renforcement</h2>
                    <p className="text-xs text-muted-foreground mb-5">Recommandations ciblées sur vos dimensions les plus faibles.</p>
                    <div className="space-y-4">
                        {result.recommendations.map(r => (
                            <div key={r.key} className="flex items-start gap-3">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                                <div>
                                    <div className="text-sm font-medium">{r.label}</div>
                                    <p className="text-sm text-foreground/70 leading-relaxed">{r.reco}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="card-soft p-7 mb-8 border-secondary/40" data-testid="score-ai-section">
                <h2 className="font-heading text-lg mb-1 flex items-center gap-2"><Sparkles size={17} className="text-secondary" /> Plan d'action Valley AI</h2>
                {aiText ? (
                    <div className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line mt-3" data-testid="score-ai-analysis">{aiText}</div>
                ) : (
                    <>
                        <p className="text-xs text-muted-foreground mb-4">
                            Obtenez un plan d'action personnalisé généré par Valley AI : diagnostic, 3 priorités et feuille de route 30/60/90 jours.
                        </p>
                        <Button onClick={getAiPlan} disabled={aiBusy || !result.id}
                            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="score-ai-btn">
                            {aiBusy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Sparkles size={14} className="mr-2" />}
                            {aiBusy ? 'Analyse en cours…' : 'Générer mon plan d\'action IA — gratuit'}
                        </Button>
                    </>
                )}
            </div>

            <div className="card-soft p-7 bg-surface text-center">
                <h2 className="font-heading text-xl mb-2">Passez du diagnostic à l'action.</h2>
                <p className="text-sm text-foreground/70 mb-6 max-w-lg mx-auto">
                    Structurez ce projet gratuitement sur African Valley 360° — GANTT, budget, GED et Valley AI inclus, sans carte bancaire.
                </p>
                <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/signup-free">
                        <Button className="bg-primary hover:bg-primary-hover h-11 px-6" data-testid="score-cta-signup">
                            Créer mon compte gratuit <ArrowRight size={14} className="ml-1.5" />
                        </Button>
                    </Link>
                    <Link to="/apply?source=demo">
                        <Button variant="outline" className="h-11 px-6" data-testid="score-cta-demo">Demander une démonstration</Button>
                    </Link>
                    <Button variant="ghost" onClick={copyLink} className="h-11" data-testid="score-share-btn">
                        <Share2 size={14} className="mr-1.5" /> Partager mon résultat
                    </Button>
                </div>
                <button type="button" onClick={onRestart} className="mt-5 text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5" data-testid="score-restart">
                    <RefreshCcw size={12} /> Refaire l'évaluation
                </button>
            </div>
        </div>
    );
}

export default function ProjectScore() {
    const [params] = useSearchParams();
    const resultId = params.get('result');
    const [dims, setDims] = useState([]);
    const [phase, setPhase] = useState(resultId ? 'loading-result' : 'intro'); // intro | quiz | contact | result
    const [current, setCurrent] = useState(0);
    const [answers, setAnswers] = useState({});
    const [contact, setContact] = useState({ project_name: '', email: '', first_name: '', last_name: '', organization: '' });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        axios.get(`${API}/project-score/questionnaire`).then(r => setDims(r.data.dimensions || [])).catch(() => { });
        if (resultId) {
            axios.get(`${API}/project-score/result/${resultId}`)
                .then(r => { setResult(r.data); setPhase('result'); })
                .catch(() => setPhase('intro'));
        }
    }, [resultId]);

    const start = () => { trackEvent('score_started'); setPhase('quiz'); setCurrent(0); setAnswers({}); };

    const answer = (dimKey, idx) => {
        const next = { ...answers, [dimKey]: idx };
        setAnswers(next);
        setTimeout(() => {
            if (current < dims.length - 1) setCurrent(current + 1);
            else setPhase('contact');
        }, 250);
    };

    const submit = async (withContact) => {
        setBusy(true);
        try {
            const payload = { answers, anon_id: getAnonId() };
            if (withContact) {
                if (contact.project_name.trim()) payload.project_name = contact.project_name.trim();
                if (contact.email.trim()) payload.email = contact.email.trim();
                if (contact.first_name.trim()) payload.first_name = contact.first_name.trim();
                if (contact.last_name.trim()) payload.last_name = contact.last_name.trim();
                if (contact.organization.trim()) payload.organization = contact.organization.trim();
            }
            const r = await axios.post(`${API}/project-score/submit`, payload);
            setResult({ ...r.data, project_name: payload.project_name });
            setPhase('result');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    const dim = dims[current];
    const progressPct = dims.length ? Math.round(((phase === 'contact' ? dims.length : current) / dims.length) * 100) : 0;
    const shareUrl = result?.id ? `${window.location.origin}/p/project-score?result=${result.id}` : window.location.href;

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b-2 border-secondary bg-surface">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3" data-testid="score-home-link">
                        <img src="/brand/african-valley-logo-96.png" alt="African Valley 360°" className="h-11 w-11 rounded-lg object-cover" />
                        <span className="flex items-baseline gap-2">
                            <span className="font-heading text-2xl"><span className="text-foreground">African</span> <span className="text-primary">Valley</span></span>
                            <span className="font-heading text-xs uppercase tracking-[0.18em] text-foreground/50 hidden sm:inline">360°</span>
                        </span>
                    </Link>
                    <Link to="/auth/signin" className="text-sm text-foreground/70 hover:text-foreground">Connexion →</Link>
                </div>
            </header>

            <main className="px-6 py-12 lg:py-16">
                {phase === 'intro' && (
                    <div className="max-w-2xl mx-auto text-center" data-testid="score-intro">
                        <div className="eyebrow mb-3 inline-flex items-center gap-1.5"><Sparkles size={12} /> African Valley Project Score™</div>
                        <h1 className="font-heading text-4xl sm:text-5xl mb-5">
                            Évaluez la solidité de <span className="text-primary">votre projet</span> en 3 minutes.
                        </h1>
                        <p className="text-foreground/70 leading-relaxed mb-8 max-w-xl mx-auto">
                            8 dimensions clés — vision, gouvernance, planification, budget, conformité, impact, risques, ancrage local —
                            notées sur 100 points. Résultat immédiat, recommandations concrètes, gratuit et sans engagement.
                        </p>
                        <div className="grid sm:grid-cols-4 gap-3 mb-10 text-xs text-foreground/65">
                            {['8 questions', 'Score sur 100', 'Recommandations ciblées', 'Lien partageable'].map((t, i) => (
                                <div key={i} className="card-soft py-3 px-2 flex items-center justify-center gap-1.5">
                                    <Check size={13} className="text-secondary" /> {t}
                                </div>
                            ))}
                        </div>
                        <Button size="lg" onClick={start} disabled={!dims.length}
                            className="bg-primary hover:bg-primary-hover h-12 px-8" data-testid="score-start-btn">
                            {dims.length ? 'Commencer l\'évaluation' : 'Chargement…'} <ArrowRight size={16} className="ml-2" />
                        </Button>
                        <p className="text-[11px] text-muted-foreground mt-4">Aucune donnée personnelle requise pour voir votre score.</p>
                    </div>
                )}

                {phase === 'quiz' && dim && (
                    <div className="max-w-2xl mx-auto" data-testid="score-quiz">
                        <div className="mb-8">
                            <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-foreground/55 mb-3">
                                <span data-testid="score-progress-label">Dimension {current + 1} / {dims.length}</span>
                                <span>{progressPct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-surface-muted overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-primary to-amber-400 transition-all duration-300" style={{ width: `${progressPct}%` }} />
                            </div>
                        </div>
                        <div className="eyebrow mb-2 text-secondary">{dim.label} · {dim.weight} points</div>
                        <h2 className="font-heading text-2xl sm:text-3xl mb-7" data-testid="score-question">{dim.question}</h2>
                        <div className="space-y-3">
                            {dim.options.map((opt, idx) => (
                                <button type="button" key={idx} onClick={() => answer(dim.key, idx)}
                                    className={`w-full text-left px-5 py-4 rounded-xl border transition-all text-sm
                                        ${answers[dim.key] === idx ? 'border-primary bg-primary-light/60 shadow-soft' : 'border-border bg-white hover:border-primary/40 hover:bg-surface-muted/40'}`}
                                    data-testid={`score-option-${dim.key}-${idx}`}>
                                    <span className="inline-flex items-center gap-3">
                                        <span className={`h-5 w-5 rounded-full border flex items-center justify-center shrink-0 ${answers[dim.key] === idx ? 'border-primary bg-primary text-white' : 'border-border'}`}>
                                            {answers[dim.key] === idx && <Check size={12} />}
                                        </span>
                                        {opt}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {current > 0 && (
                            <button type="button" onClick={() => setCurrent(current - 1)} className="mt-6 text-xs text-foreground/55 hover:text-foreground inline-flex items-center gap-1.5" data-testid="score-prev-btn">
                                <ArrowLeft size={12} /> Question précédente
                            </button>
                        )}
                    </div>
                )}

                {phase === 'contact' && (
                    <div className="max-w-xl mx-auto" data-testid="score-contact">
                        <div className="eyebrow mb-2 text-secondary">Dernière étape — optionnelle</div>
                        <h2 className="font-heading text-3xl mb-3">Recevez votre analyse détaillée.</h2>
                        <p className="text-sm text-foreground/70 mb-7 leading-relaxed">
                            Laissez un email pour être accompagné par notre équipe (optionnel) — ou affichez directement votre score.
                        </p>
                        <div className="card-soft p-6 space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Nom du projet</label>
                                <Input value={contact.project_name} onChange={e => setContact(c => ({ ...c, project_name: e.target.value }))}
                                    placeholder="ex. Centrale solaire de Thiès" data-testid="score-project-name" />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium">Prénom</label>
                                    <Input value={contact.first_name} onChange={e => setContact(c => ({ ...c, first_name: e.target.value }))} data-testid="score-first-name" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-sm font-medium">Nom</label>
                                    <Input value={contact.last_name} onChange={e => setContact(c => ({ ...c, last_name: e.target.value }))} data-testid="score-last-name" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Email professionnel</label>
                                <Input type="email" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                                    placeholder="vous@organisation.com" data-testid="score-email" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-sm font-medium">Organisation</label>
                                <Input value={contact.organization} onChange={e => setContact(c => ({ ...c, organization: e.target.value }))} data-testid="score-organization" />
                            </div>
                            <Button onClick={() => submit(true)} disabled={busy}
                                className="w-full bg-primary hover:bg-primary-hover h-11" data-testid="score-submit-btn">
                                {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : null}
                                Voir mon score <ArrowRight size={15} className="ml-1.5" />
                            </Button>
                            <button type="button" onClick={() => submit(false)} disabled={busy}
                                className="w-full text-xs text-foreground/55 hover:text-foreground py-1" data-testid="score-skip-contact">
                                Afficher mon score sans laisser mes coordonnées
                            </button>
                        </div>
                    </div>
                )}

                {phase === 'result' && result && (
                    <ResultView result={result} shareUrl={shareUrl}
                        onRestart={() => { setPhase('intro'); setResult(null); setAnswers({}); setCurrent(0); }} />
                )}

                {phase === 'loading-result' && (
                    <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>
                )}
            </main>
            <Footer />
        </div>
    );
}
